/**
 * In Page Navigation
 * It binds all links on provided container and listens to click events,
 * by identifying if it's internal sportsbook url and should navigate to it or not.
 *
 * @module app/components/InPageNavigation
 */
import _ from 'lodash';
import jQuery from 'jquery';
import PageCommon from '../pages/PageCommon';
import { trackLink } from '../libs/trackLink';

/**
 * Flag for logging uncaught events propagation
 *
 * @type {Boolean}
 *
 * @private
 */
var logEnabled = false;

/**
 * Click Event Namespace
 *
 * @type {String}
 *
 * @private
 */
var eventNamespace = 'inPageNavigation';

/**
 * Sportsbook Routing Keyword default is 'betting', if WH.sportsbook.routingKeyword is set it will override
 *
 * @type {String}
 *
 * @private
 */
var routingKeyword = 'betting';

/**
 * Sportsbook Domain WH.sportsbook.sportsDomain if is set, otherwise: window.location.hostname
 *
 * @type {String}
 *
 * @private
 */
var sportsDomain = null;

/**
 * Sportsbook domain + routing keyword:
 * WH.sportsbook.sportsDomain/WH.sportsbook.routingKeyword if is set, otherwise:
 * window.location.hostname/routingKeyword
 *
 * @type {String}
 *
 * @private
 */
var sportsbookUrlPart = null;

/**
 * Special hrefs that we don't want to bind to
 *
 * @type {Array}
 *
 * @private
 */
var specialHrefAttrs = [
    '#',
    '',
];

/**
 * Binds 'click.'+eventNamespace events on provided container, filters only 'a:not(.non-route)'
 *
 * @param {String|Object} container It can be either jQuery string selector or jQuery object
 *
 * @returns {void}
 *
 * @private
 */
var bindClickEvents = function (container) {

    if (container) {

        /**
         * Executes in page navigation, if:
         *      event.currentTarget.href is defined
         *      and link pathname is not the same as current page pathname
         *          (the above is to catch cases when href="" and href="#", as they will navigate to the same
         *          page, because of the way it builds target event's href and pathname attributes)
         *      and if it's a sportsbook internal link
         *
         * @param {Object} event jQueryEvent Object
         *
         * @returns {Boolean} it returns PageCommon.navigate value, where if it was in page navigation -
         * it will be false, or propagateEvent
         *
         * @private
         */
        var bindToNavigate = function (event) {
            const element = event.currentTarget;

            trackLink(element).then(() => {
                if (!element.href) {
                    return;
                }

                // Remove this condition: _.indexOf(specialHrefAttrs, element.pathname) === -1
                // if you don't want to catch href="" and href="#" cases and let it navigate to the same page.
                // But you should keep it, if you want to propagate these not binded links to be logged.
                // Translations: dont translate window.location.pathname, it must match links from tmpl
                if (_.indexOf(specialHrefAttrs, element.pathname) === -1 &&
                    isInPageNavigationLink(element.hostname + element.pathname)) {
                    if (window.location.href === element.href) {
                        return window.location.reload();
                    }

                    PageCommon.navigate(element.href);
                } else if(!element.target || element.target === '_self'){
                    window.location.href = element.href;
                }
            });
            
            if(element.href && (!element.target || element.target === '_self')){
                return false;
            }
        };

        jQuery(container).on('click.' + eventNamespace, 'a:not(.non-route)', bindToNavigate);

        jQuery(container).on('click.' + eventNamespace + 'non-route', 'a.non-route', function(event) {
            trackLink(event.currentTarget);
        });
    }
};

/**
 * Unbinds 'click.'+eventNamespace events from provided container
 *
 * @param {String|Object} container It can be either jQuery string selector or jQuery object
 *
 * @returns {void}
 *
 * @private
 */
var unbindClickEvents = function (container) {

    if (container) {

        jQuery(container).off('click.' + eventNamespace);
        jQuery(container).off('click.' + eventNamespace + 'non-route');
    }
};

/**
 * It checks provided link and returns true if href is valid sportsbook link, otherwise false
 * link should not include protocol or port number (hostname + pathname)
 *
 * @param {String} href Link url value
 *
 * @returns {Boolean} true if provided href is valid sportsbook link
 *
 * @private
 */
var isInPageNavigationLink = function (link) {

    if (link && link.indexOf(sportsbookUrlPart) === 0) {

        return true;
    }

    return false;
};

/**
 * It sets default params required to for a link validations and logging
 * It tries to get required information from window.WH.sportsbook, if it doesn't contain the data,
 * it will use defaults
 *
 * @returns {void}
 *
 * @private
 */
var setParams = function () {

    setSportsDomain();
    setRoutingKeyword();

    sportsbookUrlPart = sportsDomain + '/' + routingKeyword + '/';

    if (window.WH && window.WH.sportsbook && window.WH.sportsbook.inPageNavigationLog) {

        logEnabled = window.WH.sportsbook.inPageNavigationLog;
    }
};

/**
 * It sets sportsDomain from window.WH.sportsbook.sportsDomain, if it's not defined, then it's
 * window.location.hostname
 *
 * @returns {void}
 *
 * @private
 */
var setSportsDomain = function () {

    if (window.WH && window.WH.sportsbook && window.WH.sportsbook.sportsDomain) {

        sportsDomain = window.WH.sportsbook.sportsDomain;
    } else {

        sportsDomain = window.location.hostname;
    }
};

/**
 * It sets routingKeyword from window.WH.sportsbook.routingKeyword, if it's defined. Otherwise the default
 * value will remain.
 *
 * @returns {void}
 *
 * @private
 */
var setRoutingKeyword = function () {

    if (window.WH && window.WH.sportsbook && window.WH.sportsbook.routingKeyword) {

        routingKeyword = window.WH.sportsbook.routingKeyword;
    }
};

/**
 * Public functions.
 *
 * @type {Object}
 *
 * @public
 */
var api = {

    /**
     * Function inits InPageNavigation component, it sets default params and
     * binds 'click.'+eventNamespace events on provided container.
     *
     * @param {String|Object} container It can be either jQuery string selector or jQuery object, required
     * @param {Function} propagation Handler Function to be called back if event bubbling up happened, not required
     *
     * @returns {void}
     *
     * @public
     */
    init: function (container) {

        // PageCommon = (typeof mockPageCommon !== 'undefined') ? mockPageCommon : PageCommon;
        setParams();
        bindClickEvents(container);
    },

    /**
     * Unbinds click events from provided container
     *
     * @param {String|Object} container It can be either jQuery string selector or jQuery object
     *
     * @returns {void}
     *
     * @public
     */
    unbind: function (container) {

        unbindClickEvents(container);
    },
};

export default api;
    